import { useCallback, useEffect, useMemo } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useOrganization } from '@/contexts/OrganizationContext'
import { toast } from '@atrvd/saas-ui'
import {
  isLedgerSyncSyncTerminal,
  LEDGERSYNC_SYNC_PIPELINES,
  readLedgerSyncPipeline,
  type LedgerSyncSyncPipeline,
} from '@dcp/shared/ledgersync/sync-state'
import type { BankConnectionRow } from '@/lib/db-tenant'
import { useJobTransitions } from '@/hooks/useBackgroundJobStatus'
import { BANKING_KEYS } from '@/lib/query-keys'
import { useSyncStallRerender } from '@/hooks/useSyncStallRerender'
import {
  invalidateAfterBalanceSync,
  invalidateAfterTransactionSync,
} from '@/lib/ledgersync-sync-invalidation'
import { isPipelineSyncActive } from '@/lib/ledgersync-sync-activity'
import { settleSyncClicks, type SyncFinish } from '@/lib/ledgersync-sync-clicks'

/** One pipeline of one connection — the unit the watcher tracks. */
interface PipelineItem {
  row: BankConnectionRow
  pipeline: LedgerSyncSyncPipeline
}

/** Tracking key: one per connection × pipeline. */
const itemKey = (item: PipelineItem) => `${item.row.id}:${item.pipeline}`

/**
 * The identity of the pipeline's latest FINISHED run: its terminal status and
 * when it was stamped, or null while non-terminal / never synced. A change of
 * it between two polls is a whole run that went completed(old) →
 * completed(new) too fast for its active phase to be seen.
 */
function itemRevision(item: PipelineItem): string | null {
  const { status, statusAt } = readLedgerSyncPipeline(item.row, item.pipeline)
  if (!isLedgerSyncSyncTerminal(status)) return null
  return `${status}@${statusAt ?? ''}`
}

/**
 * Refresh and report after LedgerSync syncs finish (DCP-401).
 *
 * Mount ONCE per screen that shows connections (the list section, the
 * connection detail page), passing the polled `get_bank_connections` rows.
 * Each connection's two pipelines are tracked as separate items by ONE
 * {@link useJobTransitions}, so everything that finished in a snapshot arrives
 * as ONE batch and is handled once:
 *
 * - DATA: one invalidation per batch — the transactions set when any
 *   transactions pipeline finished (it is a superset of the balances set),
 *   otherwise the balances set. Per-item invalidation made ten banks finishing
 *   in one snapshot (Sync all → tab away → focus refetch) cancel and restart
 *   each other's refetches. Every finish counts, including scheduled ones.
 * - TOASTS: decided by lib/ledgersync-sync-clicks — one per click made in this
 *   tab, failures per bank for "Sync all", nothing for syncs nobody here
 *   started.
 *
 * A finish is an active → idle transition, an idle pipeline whose terminal
 * stamp moved (a run too fast to catch mid-flight), or a run going idle by
 * AGE alone. That verdict is only ever taken on a NEW snapshot: at a stall
 * deadline the watcher's clock ({@link useSyncStallRerender}) refetches the
 * list, so it comes from a row the server returned after the deadline.
 * Snapshots are taken in fetch order only, so a cached entry served on a sort
 * switch is never mistaken for a finish.
 *
 * @param connections - The connection list as last fetched; `undefined` while loading
 * @param snapshotAt - When `connections` was fetched (the query's `dataUpdatedAt`)
 */
export function useLedgerSyncCompletionWatcher(
  connections: readonly BankConnectionRow[] | undefined,
  snapshotAt: number | undefined,
): void {
  const queryClient = useQueryClient()
  const { currentOrganization } = useOrganization()
  const organizationId = currentOrganization?.id
  const stallTick = useSyncStallRerender(connections)

  /**
   * A stall is a fact about the SERVER — the row still reads non-terminal
   * after the ceiling — so it is never concluded from the snapshot already on
   * screen. Transitions are evaluated on NEW snapshots only (`snapshotAt`
   * below), and at a stall deadline the tick re-renders the screen (its
   * spinner goes idle by the clock) and asks for a fresh one. Re-judging the
   * old snapshot at the tick instead would, with polling paused in a hidden
   * tab, call a run dead that had finished meanwhile — settling its click with
   * no toast before the real result arrived.
   */
  useEffect(() => {
    if (stallTick === 0) return
    void queryClient.invalidateQueries({ queryKey: BANKING_KEYS.allBankConnections })
  }, [stallTick, queryClient])

  // Memoised on the snapshot so the transition hook compares once per new
  // data (or clock tick), not on every render.
  const items = useMemo<PipelineItem[] | undefined>(
    () => connections?.flatMap((row) => LEDGERSYNC_SYNC_PIPELINES.map((pipeline) => ({ row, pipeline }))),
    [connections],
  )

  /**
   * Handle every pipeline that finished in one snapshot.
   *
   * @param finished - The batch, each item carrying its current row
   */
  const handleFinished = useCallback(
    (finished: PipelineItem[]) => {
      if (finished.some((item) => item.pipeline === 'transactions')) {
        invalidateAfterTransactionSync(queryClient)
      } else {
        invalidateAfterBalanceSync(queryClient)
      }

      const finishes: SyncFinish[] = finished.map((item) => {
        const { status, error } = readLedgerSyncPipeline(item.row, item.pipeline)
        return {
          connectionId: item.row.id,
          pipeline: item.pipeline,
          status,
          error,
          displayName: item.row.display_name,
        }
      })
      const rowsById = new Map((connections ?? []).map((row) => [row.id, row]))
      const notices = settleSyncClicks(queryClient, organizationId, finishes, (id) => rowsById.get(id))
      for (const notice of notices) toast[notice.kind](notice.message)
    },
    [queryClient, organizationId, connections],
  )

  useJobTransitions({
    items,
    getKey: itemKey,
    // Stall-aware via the shared rule; evaluated on fresh snapshots only.
    isActive: (item) => isPipelineSyncActive(item.row, item.pipeline),
    getRevision: itemRevision,
    snapshotAt,
    onFinish: handleFinished,
  })
}
